import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSharedValidations } from '../../../../hooks/useSharedValidations';
import { message } from 'antd';
import type { UploadFile } from 'antd';
import Button from '../../../atoms/Button';
import FormField from '../../../FormField/FormField';
import { Product } from './Catalogue';
import {
  useAddCatalogueMutation,
  useEditCatalogueMutation,
  useUploadCatalogueImageMutation,
  useUploadCatalogueVideoMutation,
  useDeleteCatalogueDocumentMutation,
} from '../../../../store/services/sellerProfileCreationService';

const DESCRIPTION_SPECS_MIN = 20;
const DESCRIPTION_SPECS_MAX = 1000;

export type UploadFileWithId = UploadFile & { id?: string | number };

export default function CatalogueForm({
  profileId,
  initialData,
  onSuccess,
}: {
  profileId: string;
  initialData?: Product | null;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();

  const [addCatalogue, { isLoading: isAddLoading }] = useAddCatalogueMutation();
  const [editCatalogue, { isLoading: isEditLoading }] = useEditCatalogueMutation();
  const [uploadCatalogueImage] = useUploadCatalogueImageMutation();
  const [uploadCatalogueVideo] = useUploadCatalogueVideoMutation();
  const [deleteCatalogueDocument] = useDeleteCatalogueDocumentMutation();

  const [imagesLoading, setImagesLoading] = useState(false);
  const [videosLoading, setVideosLoading] = useState(false);

  const { documentsValidationFun } = useSharedValidations();

  const catalogueFormSchema = z.object({
    name: z
      .string({ required_error: t('components.validations.requiredField') })
      .trim()
      .min(10, t('components.validations.minLength', { value: 10 }))
      .max(50, t('components.validations.maxLength', { value: 50 })),
    description: z
      .string({ required_error: t('components.validations.requiredField') })
      .trim()
      .min(
        DESCRIPTION_SPECS_MIN,
        t('components.validations.minLength', { value: DESCRIPTION_SPECS_MIN })
      )
      .max(
        DESCRIPTION_SPECS_MAX,
        t('components.validations.maxLength', { value: DESCRIPTION_SPECS_MAX })
      ),
    specs: z
      .string({ required_error: t('components.validations.requiredField') })
      .trim()
      .min(
        DESCRIPTION_SPECS_MIN,
        t('components.validations.minLength', { value: DESCRIPTION_SPECS_MIN })
      )
      .max(
        DESCRIPTION_SPECS_MAX,
        t('components.validations.maxLength', { value: DESCRIPTION_SPECS_MAX })
      ),
    images: z.preprocess(
      (files) => {
        if (!Array.isArray(files)) return files;
        return files.map((file) => {
          if (!file?.originFileObj) {
            return { ...file, type: 'image/jpeg', size: file.size !== undefined ? file.size : file.blobSize !== undefined ? file.blobSize : 0 };
          }
          return file;
        });
      },
      documentsValidationFun(
        0,
        10,
        ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'],
        5,
        false
      )
    ),
    videos: z.preprocess(
      (files) => {
        if (!Array.isArray(files)) return files;
        return files.map((file) => {
          if (!file?.originFileObj) {
            return { ...file, type: 'video/mp4', size: file.size !== undefined ? file.size : file.blobSize !== undefined ? file.blobSize : 0 };
          }
          return file;
        });
      },
      documentsValidationFun(0, 2, ['video/mp4', 'video/webm'], 50, true)
    ),
  });

  type CatalogueFormValues = z.infer<typeof catalogueFormSchema>;

  const methods = useForm<CatalogueFormValues>({
    mode: 'all',
    resolver: zodResolver(catalogueFormSchema),
    defaultValues: {
      name: '',
      description: '',
      specs: '',
      images: [] as any[],
      videos: [] as any[],
    },
  });

  const { formState: { errors }, reset, setValue, getValues } = methods;

  // ── Populate form on edit ────────────────────────────────────────────────────

  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name,
        description: initialData.description,
        specs: initialData.specs,
        images: initialData.attachments?.filter((a) => a.type === 'IMAGE') || [],
        videos: initialData.attachments?.filter((a) => a.type === 'VIDEO') || [],
      });
    }
  }, [initialData, reset]);

  // ── Image upload / delete ────────────────────────────────────────────────────

  const uploadImages = useCallback(async () => {
    const currentList = (getValues('images') || []) as UploadFileWithId[];
    const newFiles = currentList.filter((f) => f.originFileObj && !f.id && f.uid);
    if (newFiles.length === 0) return;

    setImagesLoading(true);
    try {
      const formData = new FormData();
      newFiles.forEach((f) => {
        if (f.originFileObj) formData.append('files', f.originFileObj as Blob);
      });
      const result = await uploadCatalogueImage(formData).unwrap();
      const payload = result?.payload || [];
      let payloadIndex = 0;
      const updatedList = currentList.map((f) => {
        const fWithId = f as UploadFileWithId;
        if (f.originFileObj && !fWithId.id && payload[payloadIndex]) {
          const item = payload[payloadIndex++];
          return { ...f, id: item.id, url: item.blobFileUrl, status: 'done' } as UploadFile;
        }
        return f;
      });
      setValue('images', updatedList as any);
    } catch {
      message.error(t('components.somethingWentWrong'));
    } finally {
      setImagesLoading(false);
    }
  }, [getValues, setValue, uploadCatalogueImage, t]);

  const deleteImage = useCallback(
    async (id?: string | number) => {
      if (id == null) return;
      setImagesLoading(true);
      try {
        await deleteCatalogueDocument({ documentId: String(id) }).unwrap();
        const currentList = (getValues('images') || []) as UploadFileWithId[];
        setValue(
          'images',
          currentList.filter((f) => String(f.id) !== String(id) && f.uid !== id) as any
        );
      } catch {
        message.error(t('components.somethingWentWrong'));
      } finally {
        setImagesLoading(false);
      }
    },
    [getValues, setValue, deleteCatalogueDocument, t]
  );

  // ── Video upload / delete ────────────────────────────────────────────────────

  const uploadVideos = useCallback(async () => {
    const currentList = (getValues('videos') || []) as UploadFileWithId[];
    const newFiles = currentList.filter((f) => f.originFileObj && !f.id && f.uid);
    if (newFiles.length === 0) return;

    setVideosLoading(true);
    try {
      const formData = new FormData();
      newFiles.forEach((f) => {
        if (f.originFileObj) formData.append('files', f.originFileObj as Blob);
      });
      const result = await uploadCatalogueVideo(formData).unwrap();
      const payload = result?.payload || [];
      let payloadIndex = 0;
      const updatedList = currentList.map((f) => {
        const fWithId = f as UploadFileWithId;
        if (f.originFileObj && !fWithId.id && payload[payloadIndex]) {
          const item = payload[payloadIndex++];
          return { ...f, id: item.id, url: item.blobFileUrl, status: 'done' } as UploadFile;
        }
        return f;
      });
      setValue('videos', updatedList as any);
    } catch {
      message.error(t('components.somethingWentWrong'));
    } finally {
      setVideosLoading(false);
    }
  }, [getValues, setValue, uploadCatalogueVideo, t]);

  const deleteVideo = useCallback(
    async (id?: string | number) => {
      if (id == null) return;
      setVideosLoading(true);
      try {
        await deleteCatalogueDocument({ documentId: String(id) }).unwrap();
        const currentList = (getValues('videos') || []) as UploadFileWithId[];
        setValue(
          'videos',
          currentList.filter((f) => String(f.id) !== String(id) && f.uid !== id) as any
        );
      } catch {
        message.error(t('components.somethingWentWrong'));
      } finally {
        setVideosLoading(false);
      }
    },
    [getValues, setValue, deleteCatalogueDocument, t]
  );

  // ── Submit ───────────────────────────────────────────────────────────────────

  const onSubmit = async (data: CatalogueFormValues) => {
    try {
      const payload = {
        name: data.name,
        description: data.description ?? '',
        specs: data.specs ?? '',
        attachments: [
          ...(data.images as UploadFileWithId[]).map((i) => ({ id: i.id, type: 'IMAGE' })),
          ...((data.videos as UploadFileWithId[]) ?? []).map((v) => ({ id: v.id, type: 'VIDEO' })),
        ],
      };

      if (initialData) {
        await editCatalogue({ profileId, catalogueId: initialData.id, data: payload as any }).unwrap();
      } else {
        await addCatalogue({ profileId, data: payload as any }).unwrap();
      }
      onSuccess();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string; errors?: Record<string, string> } };
      if (error?.data?.errors) {
        Object.entries(error.data.errors).forEach(([field, msg]) => {
          // @ts-expect-error dynamic field name
          methods.setError(field, { type: 'server', message: msg });
        });
        const firstError = Object.values(error.data.errors)[0];
        if (firstError) message.error(firstError);
      } else {
        message.error(error?.data?.message || t('components.somethingWentWrong'));
      }
    }
  };

  const descriptionHint = t('components.textareaMinAndMaxLength', {
    min: DESCRIPTION_SPECS_MIN,
    max: DESCRIPTION_SPECS_MAX,
  });

  const descriptionHelperText = errors.description?.message
    ? String(errors.description.message)
    : descriptionHint;

  const specsHelperText = errors.specs?.message
    ? String(errors.specs.message)
    : descriptionHint;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} data-testid="catalogue-form">
        <div className="space-y-6 max-h-[700px] overflow-auto hidden-scrollbar" data-testid="catalogue-form-fields">
          <FormField
            type="text"
            id="name"
            name="name"
            label={t('components.catalogueItemName')}
            placeholder={t('components.catalogueItemName')}
          />
          <FormField
            type="textarea"
            id="description"
            name="description"
            label={t('components.catalogueDescription')}
            placeholder={t('components.catalogueDescription')}
            maxLength={DESCRIPTION_SPECS_MAX}
            helperText={descriptionHelperText}
          />
          <FormField
            type="textarea"
            id="specs"
            name="specs"
            label={t('components.catalogueSpecs')}
            placeholder={t('components.catalogueSpecs')}
            maxLength={DESCRIPTION_SPECS_MAX}
            helperText={specsHelperText}
          />

          <FormField
            type="file"
            id="images"
            name="images"
            maxCount={10}
            allowedTypes={['image/jpg', 'image/png', 'image/jpeg']}
            required
            label={t('components.images')}
            uploadHints={[
              `${t('components.acceptedFilesType')}: [.jpg, .png, .jpeg]`,
              t('components.maxFiles', { filesNum: 10, fileSize: 5 }),
            ]}
            uploadFileRequest={uploadImages}
            onDeleteDocument={deleteImage}
            loading={imagesLoading}
          />

          <FormField
            type="file"
            id="videos"
            name="videos"
            label={t('components.videos')}
            optional
            allowedTypes={['video/mp4', 'video/webm']}
            uploadHints={[
              `${t('components.acceptedFilesType')}: [.mp4, .webm]`,
              t('components.maxFiles', { filesNum: 2, fileSize: 50 }),
            ]}
            maxCount={2}
            maxFileSize={50}
            uploadFileRequest={uploadVideos}
            onDeleteDocument={deleteVideo}
            loading={videosLoading}
          />
        </div>

        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            disabled={isAddLoading || isEditLoading || imagesLoading || videosLoading}
            data-testid="catalogue-form-submit"
          >
            {initialData ? t('components.edit') : t('components.add')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}

